<script setup lang="ts">
import { $isCodeNode } from "@lexical/code";
import { $generateNodesFromDOM } from "@lexical/html";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $createListItemNode, $isListItemNode, $isListNode } from "@lexical/list";
import { mergeRegister } from "@lexical/utils";
import { useMagicKeys } from "@vueuse/core";
import {
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $isElementNode,
  $isLineBreakNode,
  $isRangeSelection,
  $isTextNode,
  $parseSerializedNode,
  COMMAND_PRIORITY_LOW,
  PASTE_COMMAND,
  type PasteCommandType,
} from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { onMounted, onUnmounted } from "vue";

import { isValidUrlIncludingLocal, normalizeUrl } from "~/utils/validation";

import { convertMarkdownToSerializedNodes } from "../utils";
import { clipboardHtmlIsActuallyPlainText, clipboardIsMarkdownSource, isDartInternalPaste } from "./pasteMarkdown";

const editor = useLexicalComposer();
let unregisterListeners: () => void;

const { shift, ctrl, meta } = useMagicKeys();

onMounted(() => {
  unregisterListeners = mergeRegister(
    editor.registerCommand(
      PASTE_COMMAND,
      (event: PasteCommandType) => {
        const clipboardData = event instanceof ClipboardEvent ? event.clipboardData : null;
        if (!clipboardData) {
          return false;
        }
        const text = clipboardData.getData("text/plain");
        if (!text || isDartInternalPaste(clipboardData)) {
          return false;
        }

        if (shift.value && (ctrl.value || meta.value)) {
          event.preventDefault();
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              selection.insertRawText(text);
            }
          });
          return true;
        }

        const html = clipboardData.getData("text/html");

        if (html && !clipboardIsMarkdownSource(clipboardData) && !clipboardHtmlIsActuallyPlainText(html, text)) {
          const dom = new DOMParser().parseFromString(html, "text/html");
          const nodes = $generateNodesFromDOM(editor, dom);
          const hasRichContent = nodes.some(
            (node) =>
              node.getType() !== "paragraph" ||
              ($isElementNode(node) &&
                node
                  .getChildren()
                  .some(
                    (child) =>
                      (!$isTextNode(child) && !$isLineBreakNode(child)) ||
                      ($isTextNode(child) && child.getFormat() !== 0)
                  ))
          );
          if (hasRichContent) {
            return false;
          }
        }

        const currentSelection = $getSelection();
        const url = text.trim();

        if (
          currentSelection?.getTextContent().trim() &&
          isValidUrlIncludingLocal(url) &&
          $isRangeSelection(currentSelection)
        ) {
          const node = currentSelection.anchor.getNode();
          const linkNode = $isLinkNode(node) ? node : node.getParent();
          const normalizedUrl = normalizeUrl(url);

          if ($isLinkNode(linkNode)) {
            event.preventDefault();
            editor.update(() => linkNode.setURL(normalizedUrl));
            return true;
          }

          event.preventDefault();
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, normalizedUrl);
          return true;
        }

        event.preventDefault();

        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return;
          }

          const topElement = selection.anchor.getNode().getTopLevelElementOrThrow();
          if ($isCodeNode(topElement)) {
            selection.insertRawText(text);
            return;
          }

          const anchorNode = selection.anchor.getNode();
          const listItemNode = $isListItemNode(anchorNode) ? anchorNode : anchorNode.getParent();
          const serializedNodes = convertMarkdownToSerializedNodes(text);

          if ($isListItemNode(listItemNode)) {
            const listNode = listItemNode.getParent();
            const lines = text.split("\n").filter((line) => line.trim());

            if (
              $isListNode(listNode) &&
              lines.length > 1 &&
              !serializedNodes.some((node) => node.type === "list" || node.type === "horizontal-rule")
            ) {
              const listType = listNode.getListType();

              const lastItem = lines.reduce((curr, line) => {
                const item = $createListItemNode(listType === "check" ? false : undefined);
                item.append($createParagraphNode().append($createTextNode(line.trim())));
                curr.insertAfter(item);
                return item;
              }, listItemNode);

              lastItem.selectEnd();
              return;
            }
          }

          const nodes = serializedNodes.map($parseSerializedNode);
          const isHeadingStart =
            topElement.getType() === "heading" && selection.anchor.offset === 0 && selection.isCollapsed();

          if (!isHeadingStart || !$isElementNode(nodes[0])) {
            selection.insertNodes(nodes);
            return;
          }

          selection.insertNodes(nodes[0].getChildren());

          nodes
            .slice(1)
            .reverse()
            .forEach((node) => topElement.insertBefore(node));
        });

        return true;
      },
      COMMAND_PRIORITY_LOW
    )
  );
});

onUnmounted(() => {
  unregisterListeners?.();
});
</script>

<template>
  <slot />
</template>
